import TranslateIcon from '@mui/icons-material/Translate';
import { IconButton, ListItemText, Menu, MenuItem } from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { updateProfile } from '@/api/auth';
import { useAuth } from '@/auth/useAuth';
import { SUPPORTED_LANGUAGES, type AppLanguage } from '@/i18n';

const LABEL_KEY: Record<AppLanguage, string> = {
  en: 'language.english',
  fr: 'language.french',
};

/**
 * Compact language picker for the app bar. Switching applies immediately and,
 * for a signed-in user, persists to their profile so the choice follows them
 * across devices. Anonymous visitors have it cached in localStorage only.
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const active = (i18n.resolvedLanguage ?? 'en').slice(0, 2) as AppLanguage;

  async function choose(next: AppLanguage) {
    setAnchor(null);
    await i18n.changeLanguage(next);

    if (user) {
      try {
        // updateProfile requires the always-present identity fields; only the
        // language is actually changing here.
        await updateProfile({
          name: user.name,
          phone_number: user.phone_number,
          preferred_language: next,
        });
        await refreshUser();
      } catch {
        // Non-blocking: the language already switched locally.
      }
    }
  }

  return (
    <>
      <IconButton
        aria-label={t('language.label')}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
      >
        <TranslateIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem key={language} selected={language === active} onClick={() => void choose(language)}>
            <ListItemText>{t(LABEL_KEY[language])}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
